import {SubmitEvent, useCallback, useMemo, useState} from "react";
import {styled} from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Button from "@memaro/components/Button.tsx";
import Form from "@memaro/components/Form.tsx";
import Alert from "@mui/material/Alert";
import {useAuth} from "../lib/auth.ts";
import {useFormCheckbox, useFormInput} from "@memaro/forms";
import {
    InvalidPasswordException,
    PasswordsDoNotMatch,
    UsernameExistsException,
    StepConfirmSignUp,
    nameFromError,
} from "@memaro/auth";
import {useRouter} from "../hooks/useRouter";
import {Checkbox, FormControlLabel} from "@mui/material";
import {unauthenticated} from "../hoc/auth.tsx";

const StyledStack = styled(Stack)({
    width: 'fit-content',
    margin: '0 auto'
});

const StyledTextField = styled(TextField)({
    marginTop: 30,
    minWidth: 300,
    fontFamily: "inherit"
})

const RegisterPage = unauthenticated(() => {
    const {gotoLogin, gotoRegisterConfirm} = useRouter();
    const {signUp} = useAuth();

    const [email, setEmail] = useFormInput()
    const [password, setPassword] = useFormInput()
    const [repeatPassword, setRepeatPassword] = useFormInput()
    const [acceptedTerms, setAcceptedTerms] = useFormCheckbox()
    const [acceptedPrivacy, setAcceptedPrivacy] = useFormCheckbox()
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string>("")

    const handleRegister = useCallback(async (e: SubmitEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (password != repeatPassword) {
            setError(PasswordsDoNotMatch)
            return
        }
        setLoading(true)
        try {
            const {nextStep} = await signUp({
                email: email || "",
                password: password || "",
                attributes: {
                    acceptedTerms: true,
                }
            });
            switch (nextStep.signUpStep) {
                case StepConfirmSignUp:
                    gotoRegisterConfirm()
                    break
                default:
                    console.error("Unknown step:", nextStep)
            }
        } catch (e: unknown) {
            setError(nameFromError(e))
        } finally {
            setLoading(false)
        }
    }, [email, gotoRegisterConfirm, password, repeatPassword, signUp])

    const errorText = useMemo(() => {
        switch (error) {
            case "":
                return ""
            case PasswordsDoNotMatch:
                return "Opgegeven wachtwoorden komen niet overeen"
            case InvalidPasswordException:
                return "Wachtwoord voldoet niet aan alle eisen"
            case UsernameExistsException:
                return "Gebruiker met opgegeven email adres bestaat al"
            default:
                console.error("Unknown error:", error)
                return "Onbekende fout opgetreden"
        }
    }, [error])

    return (
        <StyledStack>
            <Form onSubmit={handleRegister}>
                <h1>Registreren</h1>
                {errorText ? <Alert severity="error">{errorText}</Alert> : null}
                <StyledTextField type="email" label="Email adres" autoComplete="username" value={email} onChange={setEmail} autoFocus/>
                <StyledTextField type="password" label="Wachtwoord" autoComplete="new-password" value={password} onChange={setPassword}/>
                <StyledTextField type="password" label="Herhaal wachtwoord" autoComplete="new-password" value={repeatPassword} onChange={setRepeatPassword}/>
                <FormControlLabel
                    control={<Checkbox onChange={setAcceptedTerms} checked={acceptedTerms}/>}
                    label={<>Ik ga akkoord met de <a href="#" target="_blank">algemene voorwaarden</a></>}
                    required
                />
                <FormControlLabel
                    control={<Checkbox onChange={setAcceptedPrivacy} checked={acceptedPrivacy}/>}
                    label={<>Ik ga akkoord met de <a href="#" target="_blank">privacy verklaring</a></>}
                    required
                />
                <Box sx={{display: "flex", flexDirection: "row-reverse"}}>
                    <Box>
                        <Button sx={{marginTop: "10px"}} onClick={gotoLogin} disabled={loading}>Inloggen</Button>
                        <Button
                            color="primary"
                            type="submit"
                            sx={{marginTop: "10px", marginLeft: "10px"}}
                            loading={loading}
                            disabled={!acceptedTerms || !acceptedPrivacy}
                        >
                            Registreren
                        </Button>
                    </Box>
                </Box>
            </Form>
        </StyledStack>
    )
})

export default RegisterPage
